//import hooks
import { evolve } from 'ramda';
import { useQuery } from 'react-query';
import { useSelector } from 'hooks';

// import types
import type {
  CronJob,
  CronJobDefinition
} from './components/admin-portal/Procedures/CronJobs/CronJobsCRUDForm.types';
import {
  CostCenters,
  SurchargeOption
} from './components/admin-portal/Tables/Employer/SurchargeRelations/types';
import { FunctionFlag } from './components/admin-portal/Tables/Settings/AdminFunctions/AdminFunctions.types';
import {
  ClusterData,
  ClusterDataPreferred
} from './components/admin-portal/Tables/Settings/Clusters/types';
import { TravelCompensationType } from './components/admin-portal/Tables/TravelCosts/Arrangements/types';
import { UseQueryOptions } from 'react-query/types/react/types';
import { TravelMovementsExportData } from 'components/admin-portal/Tables/Settings/TravelMovementsExport/types';

// import utils
import { convertResponseToForm } from './components/admin-portal/Tables/CLA/PremiumPay/components/Form/utils/utils';
import { tableDataConverter } from './components/admin-portal/Tables/Settings/AdminFunctions/utils/dataConverters';
import { convertResponseForArrangementType } from './components/admin-portal/Tables/TravelCosts/Arrangements/arrangementsUtils';
import { toOptions } from 'utils/toOptions';

//import api's and mutations
import { getAuxValueGroups } from 'RestAPIs/AuxiliaryValues';
import {
  getAccountGroups,
  getAccountsByGroup,
  getAdminFunctionGroups,
  getAdminFunctions,
  getAgeHours,
  getApiAccountById,
  getApiAccounts,
  getArrangements,
  getBonus25Methods,
  getBonusTypes,
  getBrCategories,
  getBrTypes,
  getBuhHandlings,
  getCLAOptions,
  getClusters,
  getClustersPreferred,
  getContracts,
  getCostCenters,
  getCostCenterSettings,
  getCostCentersWithGivenInterval,
  getCostCentersWithoutFilter,
  getCronJobDefinitions,
  getCronJobs,
  getDistances,
  getDutyCodes,
  getDutyTypes,
  getEmployees,
  getEmployers,
  getEmployersForSelect,
  getGrantsModules,
  getHolidayHours,
  getHolidays,
  getHolidaysSpecial,
  getMaxCLASettings,
  getParentsUnitsDetails,
  getPeriodTypes,
  getPlanUnitsList,
  getPremiumPayById,
  getPremiumPays,
  getQualifications,
  getSalaryScales,
  getDayLimitArrangements,
  getServiceBrOptions,
  getSettingsGroup,
  getSurchargeRelationById,
  getSurchargeRelations,
  getSurcharges,
  getSurchargesMaintenance,
  getTravelCompensationType,
  getTravelMovementsExport,
  getUsers,
  getVatOptions
} from 'RestAPIs/admin';

// import components
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import { showSnackbar } from './components/shared';

export const useGetAdminSettings = <T>(
  groupName: string,
  options?: Record<string, unknown>
) =>
  useQuery<T>(
    [groupName],
    async () => {
      const { data } = await getSettingsGroup(groupName);

      return data;
    },
    options
  );

export const useGetCostCenterSettings = <T>(
  groupName: string,
  costCenterId: number,
  options?: UseQueryOptions<T>
) =>
  useQuery<T>({
    queryKey: ['costCenterSettings', costCenterId, groupName],
    queryFn: async () => {
      const { data } = await getCostCenterSettings(groupName, costCenterId);

      return data;
    },
    ...(options ?? {})
  });

export const useGetApiAccounts = () => {
  const { isFetching, data } = useQuery(
    ['apiAccounts', 'list'],
    async () => {
      const { data } = await getApiAccounts();
      return data;
    },
    {
      onError: () => showErrorSnackbar()
    }
  );
  return { isFetching, data };
};

export const useGetApiAccountById = (id) => {
  const { isFetching, data } = useQuery(
    ['apiAccounts', id],
    async () => {
      const { data } = await getApiAccountById(id);
      return data;
    },
    {
      enabled: !!id,
      onError: () => showErrorSnackbar('error')
    }
  );
  return { isFetching, data };
};

export const useGetAuxValueGroups = (options?: Record<string, unknown>) =>
  useQuery(
    ['auxValueGroups'],
    async () => {
      const { data } = await getAuxValueGroups();
      return data;
    },
    options
  );

export const useGetCLAOptions = () =>
  useQuery({
    queryKey: ['cla', 'options'],
    initialData: [],
    queryFn: async () => {
      const { data } = await getCLAOptions();

      return data;
    }
  });

export const useGetSalaryScales = () =>
  useQuery(['salaryScales', 'list'], async () => {
    const { data } = await getSalaryScales();

    return data;
  });

export const useGetDayLimitArrangements = () =>
  useQuery(['dayLimitArrangements', 'list'], async () => {
    const { data } = await getDayLimitArrangements();

    return data;
  });

export const useGetQualifications = (options?: Record<string, unknown>) =>
  useQuery(
    ['qualifications'],
    async () => {
      const { data } = await getQualifications();
      return data;
    },
    options
  );

export const useGetAdminFunctions = (options?: Record<string, unknown>) =>
  useQuery(
    ['adminFunctions', 'list'],
    async () => {
      const { data } = await getAdminFunctions();
      return tableDataConverter(data);
    },
    options
  );

export const useGetAdminFunctionGroups = (
  flag: FunctionFlag,
  options?: Record<string, unknown>
) =>
  useQuery(
    [flag],
    async () => {
      const { data } = await getAdminFunctionGroups(flag);

      return data;
    },
    options
  );

export const useGetHolidayHours = () => {
  const { data, isError, isFetching } = useQuery(
    ['holidayHours', 'list'],
    async () => {
      const { data } = await getHolidayHours();
      return data;
    }
  );

  return { data, isError, isFetching };
};

export const useGetEmployers = () => {
  const { data, isError, isFetching, refetch } = useQuery(
    ['employers', 'list'],
    async () => {
      const { data } = await getEmployers();
      return data;
    }
  );
  return { data, isError, isFetching, refetch };
};

export const useGetParentsUnitsDetails = () => {
  const { data, isError, isFetching } = useQuery(
    ['costcenterdetails'],
    async () => {
      const { data } = await getParentsUnitsDetails();
      return data;
    }
  );
  return { data, isError, isFetching };
};

export const useGetPlanUnits = () => {
  const { data, isError, isFetching } = useQuery(['plan', 'list'], async () => {
    const { data } = await getEmployers();
    return data;
  });

  return { data, isFetching, isError };
};

export const useGetEmployees = (enabled = true) =>
  useQuery({
    queryKey: ['employees'],
    enabled,
    queryFn: async () => {
      const { data } = await getEmployees();
      return data.map((response) => ({
        value: response['empId'],
        label: `${response.empId} - ${response.nameInformal}`
      }));
    }
  });

export const useGetContracts = (valueProp = 'id', labelProp = 'name') =>
  useQuery(['contract-kind'], async () => {
    const { data } = await getContracts();
    return data.map((response) => ({
      value: response[valueProp],
      label: response[labelProp]
    }));
  });

export const useGetBuhHandlings = () => {
  const { data, isError, isFetching } = useQuery(
    ['buhHandlings', 'list'],
    async () => {
      const { data } = await getBuhHandlings();
      return data;
    }
  );

  return { data, isError, isFetching };
};

export const useGetPeriodTypes = () => {
  const { data, isError } = useQuery(['periodTypes', 'list'], async () => {
    const { data } = await getPeriodTypes();
    return data;
  });
  return { data, isError };
};

export const useGetHolidaysSpecial = (options?: Record<string, unknown>) =>
  useQuery(
    ['holidays-special'],
    async () => {
      const { data } = await getHolidaysSpecial();
      return data;
    },
    options
  );

export const useGetPremiumPays = (options?: Record<string, unknown>) =>
  useQuery(
    ['premiumPays'],
    async () => {
      const { data } = await getPremiumPays();

      return data;
    },
    options
  );

export const useGetBonusTypes = (options?: Record<string, unknown>) =>
  useQuery(
    ['bonusTypes', 'list'],
    async () => {
      const { data } = await getBonusTypes();

      return data;
    },
    options
  );

export const useGetBonus25Methods = (options?: Record<string, unknown>) =>
  useQuery(
    ['bonus25Methods'],
    async () => {
      const { data } = await getBonus25Methods();

      return data;
    },
    options
  );

export const useGetPremiumPayById = (
  premiumPayId?: number | null,
  options?: Record<string, unknown>
) => {
  return useQuery({
    queryKey: ['premiumPay', premiumPayId],
    queryFn: async () => {
      const { data } = await getPremiumPayById(premiumPayId);

      return convertResponseToForm(data);
    },
    enabled: !!premiumPayId,
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Error while fetching data'
      });
    },
    ...options
  });
};

export const useGetSurchargesMaintenance = (
  options?: Record<string, unknown>
) =>
  useQuery(
    ['surchargesMaintenance', 'list'],
    async () => {
      const { data } = await getSurchargesMaintenance();
      return data;
    },
    options
  );

export const useGetVat = () => {
  const { data, isError, isFetching } = useQuery({
    queryKey: ['vat'],
    queryFn: async () => {
      const { data } = await getVatOptions();
      return (
        (data?.map((item) => ({
          value: item.code,
          label: item.name
        })) as Option[]) ?? []
      );
    }
  });

  return { data, isError, isFetching };
};

export const useGetServiceBr = () => {
  const { data, isError, isFetching } = useQuery({
    queryKey: ['cost-centers', 'list'],
    queryFn: async () => {
      const { data } = await getServiceBrOptions();
      return (
        (data?.map((item) => ({
          value: item.id,
          label: item.name
        })) as Option[]) ?? []
      );
    }
  });

  return { data, isError, isFetching };
};

export const useGetDistances = (options?: Record<string, unknown>) =>
  useQuery(
    ['distances', 'list'],
    async () => {
      const { data } = await getDistances();
      return data;
    },
    options
  );

export const useGetDutyCodes = () => {
  const { data, isFetching } = useQuery(['dutyCodes', 'list'], async () => {
    const { data } = await getDutyCodes();
    return data;
  });
  return { data, isFetching };
};

export const useGetDutyTypes = () => {
  const { data, isFetching } = useQuery(['duty-hour-types'], async () => {
    const { data } = await getDutyTypes();
    return data;
  });
  return { data, isFetching };
};

export const useGetHolidays = (options?: Record<string, unknown>) =>
  useQuery(
    ['holidays', 'list'],
    async () => {
      const { data } = await getHolidays();
      return data;
    },
    options
  );

export const useGetSurchargeRelation = (id) => {
  const { data, isFetching, isError } = useQuery({
    queryKey: ['surchargeRelations', id],
    queryFn: async () => {
      const { data } = await getSurchargeRelationById(id);
      return data;
    },
    enabled: !!id
  });

  return { isFetching, data, isError };
};

export const useGetSurchargeRelations = () => {
  const { data, isFetching, isError } = useQuery({
    queryKey: ['surchargeRelations', 'list'],
    queryFn: async () => {
      const { data } = await getSurchargeRelations();
      return data;
    }
  });

  return { isFetching, data, isError };
};

export const useGetSurcharges = () => {
  const { data, isFetching, isError } = useQuery({
    queryKey: ['surcharges', 'list'],
    queryFn: async () => {
      const { data } = await getSurcharges();
      return (
        (data?.map((item) => ({
          value: item.code,
          label: item.name,
          surchargeId: item.surchargeId
        })) as SurchargeOption[]) ?? []
      );
    }
  });

  return { isFetching, data, isError };
};

export const useGetCostCenters = (
  selector?: (data: CostCenters[]) => any[]
) => {
  const { data } = useQuery({
    queryKey: ['costCenters', 'list'],
    queryFn: async () => {
      const { data } = await getCostCenters();
      return data;
    },
    ...(selector && { select: selector })
  });

  return { data };
};

export const useGetAgeHours = () => {
  const { data, isFetching } = useQuery(['ageHours', 'list'], async () => {
    const { data } = await getAgeHours();
    return data;
  });
  return { data, isFetching };
};

export const getPlanUnits = () => {
  const { data, isFetching } = useQuery(['planUnits', 'list'], async () => {
    const { data } = await getPlanUnitsList();
    return data;
  });
  return { data, isFetching };
};

export const useGetBrTypeOptions = () => {
  const { data, isFetching } = useQuery(
    ['brTypes', 'list'],
    async () => {
      const { data } = await getBrTypes();
      return data;
    },
    {
      select: (data) =>
        data.map((item) => {
          return {
            value: item.code,
            label: item.name
          };
        })
    }
  );

  return { data, isFetching };
};

export const useGetBrCategoryOptions = () => {
  const { data, isFetching } = useQuery(
    ['brCategory', 'list'],
    async () => {
      const { data } = await getBrCategories();
      return data;
    },
    {
      select: (data) =>
        data.map((item) => {
          return {
            value: item.code,
            label: item.name
          };
        })
    }
  );

  return { data, isFetching };
};

export const useGetMaxCLASettings = (options?: Record<string, unknown>) =>
  useQuery(
    ['maxCLASettings', 'list'],
    async () => {
      const { data } = await getMaxCLASettings();
      return data;
    },
    options
  );

export const useGetArrangementsOptions = () => {
  const { data } = useQuery(
    ['arrangements-options', 'list'],
    async () => {
      const { data } = await getArrangements();
      return data;
    },
    {
      select: (data) =>
        data.map((item) => ({
          value: item.trvSetId,
          label: item.description
        }))
    }
  );

  return data;
};

export const useGetArrangements = (
  arrangementTypes: TravelCompensationType[],
  options?: Omit<UseQueryOptions<any>, 'queryKey'>
) =>
  useQuery(
    ['travel'],
    async () => {
      const { data } = await getArrangements();

      //@ts-ignore
      return convertResponseForArrangementType(data, arrangementTypes);
    },
    options
  );

export const useGetTravelCompensationType = (
  options?: Record<string, unknown>
) =>
  useQuery(
    ['employee-travel-compensation-type'],
    async () => {
      const { data } = await getTravelCompensationType();

      return data;
    },
    options
  );

export const useGetTravelMovementsExport = (
  options?: Record<string, unknown>
) => {
  const hasTravelMovementsOrExpenseClaimFeature = useSelector(
    (state) =>
      state.grants.hasTravelMovementsFeature ||
      state.grants.hasFeatureToggleExpenseClaims
  );
  return useQuery(
    ['travelMovementsExport', 'list'],
    async () => {
      const { data } = await getTravelMovementsExport();
      return (data as TravelMovementsExportData[])
        .map((data) => {
          data.exportCode1 ??= '';
          data.exportCode2 ??= '';
          return data;
        })
        .sort((a, b) => a.id - b.id);
    },
    {
      enabled:
        hasTravelMovementsOrExpenseClaimFeature &&
        (typeof options?.enabled === 'undefined' ? true : !!options.enabled),
      ...options
    }
  );
};

export const useGetEmployersForSelect = () => {
  const { data, isError } = useQuery({
    queryKey: ['employer', 'list'],
    initialData: [],
    queryFn: async () => {
      const { data } = await getEmployersForSelect();
      return data;
    }
  });

  return { data, isError };
};

export const useGetClusters = (options?: Record<string, unknown>) => {
  const hasClustersOrganizationGrant = useSelector(
    (state) => state.grants.hasClustersOrgGrant
  );
  return useQuery(
    ['clusters', 'list'],
    async () => {
      const { data } = await getClusters();
      return data as ClusterData[];
    },
    {
      enabled:
        hasClustersOrganizationGrant &&
        (typeof options?.enabled === 'undefined' ? true : !!options.enabled),
      ...options
    }
  );
};

export const useGetClustersPreferred = (
  brId: number,
  options?: Record<string, unknown>
) =>
  useQuery(
    ['selectedClusters', 'list', brId],
    async () => {
      const { data } = await getClustersPreferred(brId);
      return data as ClusterDataPreferred[];
    },
    options
  );

export const useGetCronJobs = (options?: Record<string, unknown>) => {
  return useQuery<CronJob[]>(
    ['cron-jobs', 'list'],
    async () =>
      await getCronJobs().then((resp) => {
        return resp.data.map(
          evolve({
            previousFireTime: (date) => (date ? new Date(date) : null),
            nextFireTime: (date) => (date ? new Date(date) : null)
          })
        );
      }),
    options
  );
};

export const useGetCronJobDefinitions = () =>
  useQuery<CronJobDefinition[]>(['cron-jobs', 'definitions'], async () => {
    const { data } = await getCronJobDefinitions();
    return data;
  });

export const useGetGrantsModules = () => {
  const { data, isFetching } = useQuery({
    queryKey: ['modules', 'list'],
    queryFn: async () => {
      const { data } = await getGrantsModules();
      return data;
    }
  });
  return { data, isFetching };
};

export const useGetEmployeeCostCenter = () =>
  useQuery({
    queryKey: ['costcenters'],
    initialData: [],
    queryFn: async () => {
      const { data } = await getCostCentersWithoutFilter();
      return data;
    }
  });

export const useGetAccountGroups = () =>
  useQuery({
    queryKey: ['account-groups'],
    initialData: undefined,
    queryFn: async () => {
      const { data } = await getAccountGroups();
      return data;
    }
  });
export const useGetAccountGroupsOptions = () =>
  useQuery({
    queryKey: ['account-groups-options'],
    initialData: [],
    queryFn: async () => {
      const { data } = await getAccountGroups();
      return toOptions(data, 'groupId', 'name');
    }
  });

export const useGetCostCentersWithoutFilter = () =>
  useQuery({
    queryKey: ['costcenters'],
    initialData: [],
    queryFn: async () => {
      const { data } = await getCostCentersWithoutFilter();
      return data;
    }
  });

const costCenterItemsToOption = (
  data: CostCenters[],
  completeLabel: boolean
) => {
  return data.map((option) => ({
    label: completeLabel
      ? `${option.name} - ${option.nameShort} - ${option.code}`
      : option.nameShort,
    value: option.brId
  }));
};

export const useGetCostCentersWithGivenInterval = (
  start: string,
  end: string,
  enabled = true,
  completeLabel = false
) =>
  useQuery({
    queryKey: ['costcenters', 'options'],
    enabled,
    initialData: [],
    queryFn: async () => {
      const { data } = await getCostCentersWithGivenInterval(start, end);
      return costCenterItemsToOption(data, completeLabel);
    }
  });

export const useGetAccountsByGroup = (
  groupId: number,
  options?: Record<string, unknown>
) =>
  useQuery(
    ['accounts-by-group', groupId],
    async () => {
      const { data } = await getAccountsByGroup(groupId);

      return data;
    },
    options
  );

export const useGetUsers = (options?: Record<string, unknown>) =>
  useQuery(
    ['users'],
    async () => {
      const { data } = await getUsers();

      return data;
    },
    options
  );
